/**
 * A formula, drawn.
 *
 * The last step of three: `parse.ts` reads the source into structure, `layout.ts`
 * works out where every piece goes, and this draws the result through
 * `react-native-svg` — the same library the app already draws its icons, its
 * animation and its matching exercise with, on the web and on both phones. No
 * WebView, no second rendering pipeline, and no LaTeX engine in the bundle.
 *
 * An SVG is the right target for typesetting for one reason above the others:
 * **a formula is positioned, not flowed.** A fraction's numerator is at a
 * measured offset from a bar, a subscript is at a measured offset from its base.
 * Laying text out and hoping the line break falls in the right place is what
 * makes a formula in a text field come out wrong; here every glyph is placed, and
 * a nested fraction is placed by the same arithmetic as a flat one.
 *
 * Which leaves the question of *measurement*, and that is the interesting one.
 * The engine needs widths before it can place anything, and this component gets
 * them from the platform that will draw the glyphs (`measure.tsx`), because a
 * formula in a themed book is set in the theme's face and is full of symbols that
 * face does not carry. On the first frame the widths are estimates; the probe
 * reports the truth; the layout settles. Nothing else in the block changes.
 */
import {useCallback, useMemo, useState} from 'react'
import {type LayoutChangeEvent, StyleSheet, View} from 'react-native'
import {G, Path, Rect, Svg, Text as SvgText} from 'react-native-svg'

import {layoutFormula, type Measure} from './layout'
import {
  estimateWidth,
  type FormulaLeaf,
  texMeasure,
  useTexWidths,
} from './measure'
import {type TexNode} from './parse'

export type FormulaNotationProps = {
  /** The formula's structure, from `parseFormula`. */
  node: TexNode
  /** The size the formula is set at, before any nesting reduces it. */
  size: number
  /** The ink. A formula is one colour; the theme decides which. */
  color: string
  /** The face the page is set in, so the notation belongs to the book. */
  fontFamily?: string
  /** How much of the run the formula should take. */
  align?: 'center' | 'flex-start' | 'flex-end'
  /**
   * A ruling the formula's baseline should land on, in px.
   *
   * Set by a theme that prints on lined paper, where the one thing a formula has
   * to look like is *written on the line*. The baseline sits a measured distance
   * below the top of the drawing, which is rarely a whole number of rules, so the
   * drawing is padded until it is — the same adjustment a line of type gets from
   * its line box, done here because nothing else is laying this out.
   */
  baselineStep?: number
  /** A test id for the whole drawing, so a gate can find it. */
  testID?: string
}

export function FormulaNotation({
  node,
  size,
  color,
  fontFamily,
  align = 'center',
  baselineStep,
  testID,
}: FormulaNotationProps) {
  const font = useMemo(() => ({fontFamily}), [fontFamily])

  /*
   * What the engine is going to ask for, asked for.
   *
   * The leaves are collected by *laying the formula out* with a measure that
   * records what it was asked about, rather than by walking the tree a second
   * time: the sizes and slants here are then the ones the engine itself decided,
   * for the same nesting depth and the same style, and the two cannot drift apart.
   * A second walk would be a second place for the style rules to live.
   */
  const leaves = useMemo(() => {
    const seen = new Map<string, FormulaLeaf>()
    const record: Measure = (text, {italic, size: leafSize}) => {
      const key = `${text}\u0000${italic ? 'i' : 'n'}\u0000${leafSize}`
      if (!seen.has(key)) seen.set(key, {text, italic, size: leafSize})
      return estimateWidth(text, leafSize)
    }
    layoutFormula(node, {size, measure: record})
    return [...seen.values()]
  }, [node, size])

  const {widths, probe} = useTexWidths(font, leaves)
  const measure = useMemo(() => texMeasure(font, widths), [font, widths])
  const list = useMemo(
    () => layoutFormula(node, {size, measure}),
    [node, size, measure],
  )

  /*
   * A formula wider than its block is scaled down rather than clipped, which is
   * what a viewBox is for. The room available is the block's inner width, so it
   * is measured rather than assumed; until it is known the formula is drawn at its
   * natural size, which is right for every formula that fits.
   */
  const [available, setAvailable] = useState<number | null>(null)
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width
    setAvailable(width > 0 ? width : null)
  }, [])

  const natural = {width: list.width, height: list.height + list.depth}
  const scale =
    available && natural.width > available ? available / natural.width : 1
  // Snap the baseline to the ruling, after scaling, since the ruling is a fixed
  // number of device pixels and the drawing is not.
  const baseline = list.height * scale
  const padTop =
    baselineStep && baselineStep > 0
      ? (baselineStep - (baseline % baselineStep)) % baselineStep
      : 0
  if (!natural.width || !natural.height) return null

  return (
    <View
      testID={testID}
      onLayout={onLayout}
      style={[styles.wrap, {alignItems: align, paddingTop: padTop}]}>
      {/* The probe is rendered once, beside the drawing it measures for. */}
      {probe}
      <Svg
        width={natural.width * scale}
        height={natural.height * scale}
        viewBox={`0 0 ${natural.width.toFixed(3)} ${natural.height.toFixed(3)}`}>
        {/* The origin is the baseline, so the whole drawing moves down by the
            height above it and every glyph keeps the y the engine gave it. */}
        <G transform={`translate(0, ${list.height.toFixed(3)})`}>
          {list.rects.map((rect, i) => (
            <Rect
              key={`r${i}`}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              fill={color}
            />
          ))}
          {list.strokes.map((stroke, i) => (
            <Path
              key={`s${i}`}
              d={stroke.points
                .map(
                  ([x, y], j) =>
                    `${j === 0 ? 'M' : 'L'} ${x.toFixed(3)} ${y.toFixed(3)}`,
                )
                .join(' ')}
              stroke={color}
              strokeWidth={stroke.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          ))}
          {list.runs.map((run, i) => (
            <SvgText
              key={`t${i}`}
              x={run.x}
              y={run.y}
              fontSize={run.size}
              fontFamily={fontFamily}
              /*
               * `y` is the baseline in SVG, which is exactly what the engine
               * places glyphs against — so no baseline adjustment is needed, and
               * none is done.
               */
              fontStyle={run.italic ? 'italic' : 'normal'}
              fill={color}>
              {run.text}
            </SvgText>
          ))}
        </G>
      </Svg>
    </View>
  )
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
  },
})
